import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin, Calendar, ShoppingCart, UserX, UserCheck, Edit2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../components/Modal';
import Form, { FormField } from '../components/Form';
import { useFetch } from '../hooks';
import { patientService, doctorService, appointmentService } from '../services/api';
import { getStatusColor, formatDate, formatCurrency } from '../utils/helpers';

const today = new Date().toISOString().split('T')[0];

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showEdit, setShowEdit] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const [bookForm, setBookForm] = useState({
    doctor_id: '', slot_id: '', appointment_date: '',
    consultancy_type: 'online', reason: '', clinic_note: '', reports: [],
  });
  const [availability,  setAvailability]  = useState([]);
  const [slotsLoading,  setSlotsLoading]  = useState(false);
  const [bookLoading,   setBookLoading]   = useState(false);

  const { data: patientData, loading, refetch } = useFetch(() => patientService.getById(id), [id]);
  const { data: doctors } = useFetch(() => doctorService.getAll());

  const patient      = patientData?.profile || null;
  const addresses    = patientData?.addresses || [];
  const orderSum     = patientData?.order_summary || {};
  const apptSum      = patientData?.appointment_summary || {};
  const recentOrders = patientData?.recent_orders || [];
  const recentAppts  = patientData?.recent_appointments || [];

  const selectedBookDoctor = (doctors || []).find(d => d.id === parseInt(bookForm.doctor_id));
  const allSlots = availability.flatMap(day =>
    day.is_available ? day.slots.map(s => ({ ...s, day: day.day })) : []
  );

  const handleDoctorChange = async (doctorId) => {
    setBookForm(p => ({ ...p, doctor_id: doctorId, slot_id: '' }));
    setAvailability([]);
    if (!doctorId) return;
    setSlotsLoading(true);
    try {
      const avail = await doctorService.getAvailability(doctorId);
      setAvailability(avail);
    } catch { toast.error('Failed to load slots'); }
    finally { setSlotsLoading(false); }
  };

  const handleBook = async () => {
    if (!bookForm.doctor_id || !bookForm.slot_id || !bookForm.appointment_date || !bookForm.consultancy_type) {
      toast.error('Please fill all required fields'); return;
    }
    setBookLoading(true);
    try {
      const res = await appointmentService.book({ ...bookForm, user_id: id });
      toast.success(res?.message || 'Appointment booked!');
      setShowBook(false);
      setBookForm({ doctor_id: '', slot_id: '', appointment_date: '', consultancy_type: 'online', reason: '', clinic_note: '', reports: [] });
      setAvailability([]);
      refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to book'); }
    finally { setBookLoading(false); }
  };

  const handleToggleStatus = async () => {
    const newStatus = patient.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await patientService.toggleStatus(patient.id, newStatus);
      toast.success(res?.message || 'Status updated');
      refetch();
    } catch { toast.error('Failed'); }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    const values = Object.fromEntries(new FormData(e.target));
    try {
      const res = await patientService.update(id, values);
      toast.success(res?.message || 'Patient updated!');
      setShowEdit(false);
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Update failed');
    }
  };

  if (loading) return (
    <div className="space-y-4">
      <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      <div className="card p-6 h-40 animate-pulse bg-gray-200 dark:bg-gray-700 rounded-xl" />
    </div>
  );

  if (!patient) return (
    <div className="text-center py-20">
      <p className="text-gray-500">Patient not found.</p>
      <button onClick={() => navigate('/patients')} className="btn-primary mt-4">Back to Patients</button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/patients')} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Patient Profile</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Detailed view and medical history</p>
        </div>
      </div>

      {/* Profile Card */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="w-20 h-20 bg-purple-100 dark:bg-purple-900 rounded-2xl flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-3xl shrink-0">
            {patient.profile_img
              ? <img src={patient.profile_img} alt={patient.name} className="w-full h-full object-cover rounded-2xl" />
              : patient.name[0]
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{patient.name}</h2>
                <p className="text-xs text-gray-500 mt-0.5">{patient.uid}</p>
                <span className={`badge mt-1 ${getStatusColor(patient.status)}`}>{patient.status}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setShowBook(true)} className="btn-primary flex items-center gap-1.5 text-sm">
                  <Plus className="w-4 h-4" />Book Appointment
                </button>
                <button onClick={() => setShowEdit(true)}
                  className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 bg-white dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-50 transition-colors">
                  <Edit2 className="w-4 h-4" />Edit
                </button>
                <button onClick={handleToggleStatus}
                  className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors ${patient.status === 'active' ? 'border-red-300 text-red-600 bg-red-50 dark:bg-red-900/20 dark:border-red-700' : 'border-green-300 text-green-600 bg-green-50 dark:bg-green-900/20 dark:border-green-700'}`}>
                  {patient.status === 'active' ? <><UserX className="w-4 h-4" />Suspend</> : <><UserCheck className="w-4 h-4" />Activate</>}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {[
                { icon: Mail,     label: patient.email },
                { icon: Phone,    label: patient.mobile },
                { icon: Calendar, label: `${patient.gender || '—'} | ${patient.blood_group || '—'}` },
                { icon: MapPin,   label: addresses[0] ? `${addresses[0].city}, ${addresses[0].state}` : 'No address' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Icon className="w-4 h-4 shrink-0 text-gray-400" />
                  <span className="truncate">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Orders',     value: orderSum.total_orders || 0,                color: 'text-medihub-primary dark:text-medihub-highlight' },
          { label: 'Total Spent',      value: formatCurrency(orderSum.total_spent || 0), color: 'text-green-600 dark:text-green-400' },
          { label: 'Delivered',        value: orderSum.delivered    || 0,                color: 'text-purple-600 dark:text-purple-400' },
          { label: 'Cancelled Orders', value: orderSum.cancelled    || 0,                color: 'text-red-600 dark:text-red-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card p-4 text-center">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Appointment Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total Appointments', value: apptSum.total_appointments || 0, color: 'text-gray-900 dark:text-white' },
          { label: 'Completed',          value: apptSum.completed          || 0, color: 'text-green-600 dark:text-green-400' },
          { label: 'Cancelled',          value: apptSum.cancelled          || 0, color: 'text-red-600 dark:text-red-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card p-4 text-center">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Recent Appointments */}
      <div className="card overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700 px-4 pt-4 pb-3">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Recent Appointments</p>
        </div>
        <div className="overflow-x-auto">
          {recentAppts.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">No appointments found</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>{['Doctor', 'Specialization', 'Date', 'Type', 'Fee', 'Status'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {recentAppts.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">{a.doctor_name}</td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{a.specialization}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(a.appointment_date?.split('T')[0])}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">{a.consultancy_type}</td>
                    <td className="px-4 py-3 font-semibold whitespace-nowrap">{formatCurrency(a.consultation_fee)}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><span className={`badge ${getStatusColor(a.status)}`}>{a.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="card overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700 px-4 pt-4 pb-3">
          <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2"><ShoppingCart className="w-4 h-4" />Recent Orders</p>
        </div>
        <div className="overflow-x-auto">
          {recentOrders.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">No orders found</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>{['Order ID', 'Amount', 'Payment', 'Date', 'Status'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {recentOrders.map(o => (
                  <tr key={o.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3 font-mono text-medihub-primary dark:text-medihub-highlight font-medium whitespace-nowrap">{o.uid}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white whitespace-nowrap">{formatCurrency(o.total_amount)}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 uppercase whitespace-nowrap">{o.payment_method}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-400">{formatDate(o.created_at?.split('T')[0])}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><span className={`badge ${getStatusColor(o.status)} capitalize`}>{o.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Addresses */}
      {addresses.length > 0 && (
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-purple-500" />Saved Addresses
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {addresses.map(addr => (
              <div key={addr.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase text-medihub-primary dark:text-medihub-highlight">{addr.label}</span>
                  {addr.is_default === 1 && <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full">Default</span>}
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{addr.name}</p>
                <p className="text-xs text-gray-500 mt-1">{addr.address_line}</p>
                <p className="text-xs text-gray-500">{addr.city}, {addr.state} - {addr.pincode}</p>
                <p className="text-xs text-gray-500">{addr.mobile}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Book Appointment Modal */}
      <Modal isOpen={showBook} onClose={() => setShowBook(false)} title="Book Appointment" size="lg">
        <div className="space-y-4">
          {/* Patient — pre-filled, read only */}
          <div className="p-3 bg-medihub-light dark:bg-medihub-dark/20 rounded-xl flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">Patient</span>
            <span className="font-semibold text-medihub-primary dark:text-medihub-highlight">{patient.name} ({patient.uid})</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Doctor" required>
              <select value={bookForm.doctor_id} onChange={e => handleDoctorChange(e.target.value)} className="input-field">
                <option value="">Select doctor</option>
                {(doctors || []).map(d => <option key={d.id} value={d.id}>{d.name} — {d.specialization}</option>)}
              </select>
            </FormField>

            {selectedBookDoctor && (
              <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                <span className="text-sm text-gray-500">Consultation Fee</span>
                <span className="font-bold text-green-600 dark:text-green-400 text-lg">
                  {formatCurrency(selectedBookDoctor.consultation_fee || selectedBookDoctor.fees || 0)}
                </span>
              </div>
            )}

            <FormField label="Slot" required>
              <select value={bookForm.slot_id} onChange={e => setBookForm(p => ({ ...p, slot_id: e.target.value }))}
                className="input-field" disabled={slotsLoading || !bookForm.doctor_id}>
                <option value="">
                  {slotsLoading ? 'Loading slots...' : !bookForm.doctor_id ? 'Select doctor first' : allSlots.length === 0 ? 'No slots available' : 'Select slot'}
                </option>
                {allSlots.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.day.charAt(0).toUpperCase() + s.day.slice(1)} — {s.start_time.slice(0,5)} to {s.end_time.slice(0,5)}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Appointment Date" required>
              <input type="date" value={bookForm.appointment_date} min={today}
                onChange={e => setBookForm(p => ({ ...p, appointment_date: e.target.value }))} className="input-field" />
            </FormField>

            <FormField label="Consultancy Type" required>
              <select value={bookForm.consultancy_type} onChange={e => setBookForm(p => ({ ...p, consultancy_type: e.target.value }))} className="input-field">
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
            </FormField>

            <FormField label="Reason">
              <input value={bookForm.reason} onChange={e => setBookForm(p => ({ ...p, reason: e.target.value }))}
                className="input-field" placeholder="Reason for visit" />
            </FormField>
          </div>

          <FormField label="Clinic Note">
            <textarea value={bookForm.clinic_note} onChange={e => setBookForm(p => ({ ...p, clinic_note: e.target.value }))}
              className="input-field resize-none" rows={2} placeholder="Optional clinic note..." />
          </FormField>

          <FormField label="Reports (max 5 files)">
            <input type="file" accept="image/*,.pdf" multiple className="input-field"
              onChange={e => setBookForm(p => ({ ...p, reports: Array.from(e.target.files).slice(0, 5) }))} />
          </FormField>

          <div className="flex justify-end gap-3 pt-1">
            <button onClick={() => setShowBook(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleBook} disabled={bookLoading}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              {bookLoading ? 'Booking...' : 'Book Appointment'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Patient Modal */}
      <Modal isOpen={showEdit} onClose={() => setShowEdit(false)} title="Edit Patient" size="md">
        <Form onSubmit={handleEditSave}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Full Name">
              <input name="name" defaultValue={patient?.name || ''} className="input-field" />
            </FormField>
            <FormField label="Email">
              <input name="email" type="email" defaultValue={patient?.email || ''} className="input-field" />
            </FormField>
            <FormField label="Mobile">
              <input name="mobile" defaultValue={patient?.mobile || ''} className="input-field" />
            </FormField>
            <FormField label="Gender">
              <select name="gender" defaultValue={patient?.gender || ''} className="input-field">
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </FormField>
            <FormField label="Date of Birth">
              <input name="dob" type="date" defaultValue={patient?.dob?.split('T')[0] || ''} className="input-field" />
            </FormField>
            <FormField label="Blood Group">
              <select name="blood_group" defaultValue={patient?.blood_group || ''} className="input-field">
                <option value="">Select</option>
                {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
              </select>
            </FormField>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShowEdit(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Update Patient</button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default PatientDetail;
